import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    TextField,
    Button,
    Grid,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Alert,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LogoutIcon from '@mui/icons-material/Logout';
import axios from '../../utils/axios';

const Settings = () => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deviceToDelete, setDeviceToDelete] = useState(null);

    useEffect(() => {
        fetchDevices();
    }, []);

    const fetchDevices = async () => {
        try {
            const response = await axios.get('/api/devices');
            setDevices(response.data);
            setLoading(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Error fetching devices');
            setLoading(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setPasswordError('');
        setPasswordSuccess('');

        if (newPassword !== confirmPassword) {
            setPasswordError('New passwords do not match');
            return;
        }

        try {
            await axios.put('/api/users/password', {
                currentPassword,
                newPassword
            });
            setPasswordSuccess('Password updated successfully');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            setPasswordError(err.response?.data?.message || 'Error updating password');
        }
    };

    const handleDeleteDevice = async () => {
        try {
            await axios.delete(`/api/devices/${deviceToDelete._id}`);
            setDevices(devices.filter(device => device._id !== deviceToDelete._id));
            setDeleteDialogOpen(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Error removing device');
        }
    };

    const handleLogoutDevice = async (deviceId) => {
        try {
            await axios.put(`/api/devices/${deviceId}/logout`);
            setDevices(devices.map(device => 
                device._id === deviceId 
                    ? { ...device, loggedIn: false }
                    : device
            ));
        } catch (err) {
            setError(err.response?.data?.message || 'Error logging out device');
        }
    };

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Typography>Loading...</Typography>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Typography variant="h4" gutterBottom>
                Settings
            </Typography>

            {/* Password Change Section */}
            <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                    Change Password
                </Typography>
                <form onSubmit={handlePasswordChange}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                type="password"
                                label="Current Password"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                type="password"
                                label="New Password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                type="password"
                                label="Confirm New Password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            {passwordError && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {passwordError}
                                </Alert>
                            )}
                            {passwordSuccess && (
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    {passwordSuccess}
                                </Alert>
                            )}
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                            >
                                Update Password
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>

            {/* Device Management Section */}
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Device Management
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                    You can have up to 3 devices logged in to your account at a time.
                </Typography>
                <List>
                    {devices.map((device) => (
                        <React.Fragment key={device._id}>
                            <ListItem>
                                <ListItemText
                                    primary={device.deviceName}
                                    secondary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" component="span">
                                                Last used: {new Date(device.lastUsed).toLocaleString()}
                                            </Typography>
                                            {device.loggedIn && (
                                                <Chip
                                                    label="Currently Logged In"
                                                    color="success"
                                                    size="small"
                                                />
                                            )}
                                        </Box>
                                    }
                                />
                                <ListItemSecondaryAction>
                                    {device.loggedIn && (
                                        <IconButton
                                            edge="end"
                                            aria-label="logout"
                                            onClick={() => handleLogoutDevice(device._id)}
                                            sx={{ mr: 1 }}
                                        >
                                            <LogoutIcon />
                                        </IconButton>
                                    )}
                                    <IconButton
                                        edge="end"
                                        aria-label="delete"
                                        onClick={() => {
                                            setDeviceToDelete(device);
                                            setDeleteDialogOpen(true);
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                            <Divider />
                        </React.Fragment>
                    ))}
                </List>
            </Paper>

            {/* Delete Device Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Remove Device</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to remove this device from your account?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteDevice} color="error">
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Settings; 